import * as React from 'react';

import type {SavedFilter} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {LockIcon, RocketIcon} from 'shells/icon';

import {FilterShareButton} from '../../filtering/share-modal/filter-share-button';

import type {ParentType} from './title-bar-save-controls-container';
import {TitleBarShareButtonUpsell} from './title-bar-share-button-upsell';

import styles from './title-bar-save-controls.module.css';

export type SavedFilterHumanReadableType = 'report' | 'list';

type Props = {
    activeSavedFilter: SavedFilter<any> | null | undefined;
    usersAndTeams: any[];
    savedFilterHumanReadableType: SavedFilterHumanReadableType;
    hasSaveControls?: boolean;
    canResave: boolean;
    isResaving: boolean;
    onSavedFiltersButtonClick: () => any;
    onOpenSharePopover: () => any;
    onOpenSavePopover: () => any;
    onResaveFilter: (id: string) => any;
    getSaveAnchor: (arg1?: HTMLElement | null | undefined) => any;
    getSaveAsAnchor: (arg1?: HTMLElement | null | undefined) => any;
    getShareAnchor: (arg1?: HTMLElement | null | undefined) => any;
    parentType: ParentType;
};

export class TitleBarSaveControls extends React.PureComponent<Props> {
    static defaultProps = {
        hasSaveControls: true,
    };

    render() {
        return (
            <>
                <Button
                    id='saved-filters-btn'
                    variant='secondary'
                    size='big'
                    disabled={!this.props.hasSaveControls}
                    onClick={this.props.onSavedFiltersButtonClick}
                    data-tip={`Saved ${this.props.savedFilterHumanReadableType}s`}
                >
                    {`My saved ${this.props.savedFilterHumanReadableType}s`}
                </Button>
                {!NutClientConfig.hasSharedListsFF &&
                    (!NutClientConfig.hasSharableListsAndReports ? (
                        <TitleBarShareButtonUpsell parentType={this.props.parentType} />
                    ) : (
                        <Button
                            variant='secondary'
                            size='big'
                            getButtonRef={this.props.getShareAnchor}
                            disabled={
                                !this.props.hasSaveControls ||
                                !this.props.activeSavedFilter ||
                                !this.props.activeSavedFilter.isUpdateable
                            }
                            onClick={this.props.onOpenSharePopover}
                            data-tip={getShareTooltip(
                                this.props.usersAndTeams,
                                this.props.activeSavedFilter
                            )}
                        >
                            <div className={styles.share}>
                                {this.getShareIcon()}
                                <span>Share</span>
                            </div>
                        </Button>
                    ))}
                {NutClientConfig.hasSharedListsFF && (
                    <FilterShareButton savedFilter={this.props.activeSavedFilter} />
                )}
                <Button
                    variant='secondary'
                    size='big'
                    disabled={
                        !this.props.hasSaveControls ||
                        !this.props.activeSavedFilter ||
                        this.props.activeSavedFilter.isHidden
                    }
                    data-tip={`Save new ${this.props.savedFilterHumanReadableType}`}
                    getButtonRef={this.props.getSaveAsAnchor}
                    onClick={this.props.onOpenSavePopover}
                >
                    Save as
                </Button>
                <Button
                    disabled={!this.props.hasSaveControls || !this.props.canResave}
                    variant='save'
                    size='big'
                    getButtonRef={this.props.getSaveAnchor}
                    onClick={this.handleSaveButtonClick}
                    data-tip={`Save ${this.props.savedFilterHumanReadableType}`}
                >
                    {this.props.isResaving ? 'Saving…' : 'Save'}
                </Button>
            </>
        );
    }

    handleSaveButtonClick = () => {
        const activeSavedFilter = this.props.activeSavedFilter;

        if (activeSavedFilter && activeSavedFilter.id && !activeSavedFilter.isHidden) {
            this.props.onResaveFilter(activeSavedFilter.id);
        } else {
            this.props.onOpenSavePopover();
        }
    };

    getShareIcon = () => {
        if (
            !this.props.hasSaveControls ||
            !this.props.activeSavedFilter ||
            !this.props.activeSavedFilter.isUpdateable
        ) {
            return undefined;
        }

        return this.props.activeSavedFilter.isShared ? (
            <RocketIcon className={styles['share-icon']} />
        ) : (
            <LockIcon className={styles['share-icon']} />
        );
    };
}

export const getShareTooltip = (
    usersAndTeams: any[],
    activeSavedFilter?: SavedFilter<any> | null
): string => {
    return !activeSavedFilter || !activeSavedFilter.isShared
        ? 'Private to only me'
        : 'Shared with everyone';
};
